import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { getUserRestaurant } from '../utils/restaurantUtils';
import AdminNavigation from '../components/AdminNavigation';
import { Plus, Users, Edit2, Trash2, QrCode, Loader } from 'lucide-react';
import QRCodeGenerator from '../components/QRCodeGenerator';

function TableManagement() {
  const navigate = useNavigate();
  const [restaurantId, setRestaurantId] = useState(null);
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [showQRCode, setShowQRCode] = useState(null);
  const [formData, setFormData] = useState({ number: '', capacity: 4 });

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      const resId = await getUserRestaurant(auth.currentUser.uid);
      setRestaurantId(resId || 'demo-restaurant');
    };
    fetchRestaurant();
  }, [navigate]);

  useEffect(() => {
    if (!restaurantId) return;

    // Subscribe to tables
    const tablesQuery = query(collection(db, 'restaurants', restaurantId, 'tables'));
    const unsubscribeTables = onSnapshot(tablesQuery, (snapshot) => {
      const tablesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTables(tablesData.sort((a, b) => a.number - b.number));
      setLoading(false);
    });

    // Subscribe to active orders
    const ordersQuery = query(collection(db, 'restaurants', restaurantId, 'orders'));
    const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
      const ordersData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(order => order.status !== 'completed');
      setOrders(ordersData);
    });

    return () => {
      unsubscribeTables();
      unsubscribeOrders();
    };
  }, [restaurantId]);

  const getTableStatus = (tableId) => {
    // Only consider non-completed orders for table status
    // Compare as strings to handle both string and number tableIds
    const activeOrder = orders.find(o => String(o.tableId) === String(tableId) && o.status !== 'completed');
    if (!activeOrder) return 'available';
    if (activeOrder.status === 'ready') return 'billing';
    return 'occupied';
  };

  const getTableOrders = (tableId) => {
    // Only show active (non-completed) orders
    // Compare as strings to handle both string and number tableIds
    return orders.filter(o => String(o.tableId) === String(tableId) && o.status !== 'completed');
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-gray-100 text-gray-600 border-gray-300',
      occupied: 'bg-blue-100 text-blue-700 border-blue-300',
      billing: 'bg-orange-100 text-orange-700 border-orange-300'
    };
    return colors[status] || colors.available;
  };

  const getStatusIcon = (status) => {
    const icons = {
      available: '🟢',
      occupied: '🔵',
      billing: '🟠'
    };
    return icons[status] || '⚪';
  };

  const handleAddTable = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'restaurants', restaurantId, 'tables'), {
        number: formData.number,
        capacity: parseInt(formData.capacity),
        status: 'available',
        qrCodeUrl: `${window.location.origin}/menu/${restaurantId}/${formData.number}`
      });
      setShowAddModal(false);
      setFormData({ number: '', capacity: 4 });
    } catch (error) {
      console.error('Error adding table:', error);
      alert('Failed to add table');
    }
  };

  const handleUpdateTable = async (e) => {
    e.preventDefault();
    try {
      const tableRef = doc(db, 'restaurants', restaurantId, 'tables', editingTable.id);
      await updateDoc(tableRef, {
        number: formData.number,
        capacity: parseInt(formData.capacity)
      });
      setEditingTable(null);
      setFormData({ number: '', capacity: 4 });
    } catch (error) {
      console.error('Error updating table:', error);
      alert('Failed to update table');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!confirm('Are you sure you want to delete this table?')) return;
    try {
      await deleteDoc(doc(db, 'restaurants', restaurantId, 'tables', tableId));
    } catch (error) {
      console.error('Error deleting table:', error);
      alert('Failed to delete table');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {/* Add Table Button */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Table Management</h2>
            <p className="text-sm text-gray-600">Manage restaurant tables and layout</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Add Table</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Status Legend */}
            <div className="card mb-6">
              <h3 className="text-sm font-semibold mb-2">Status Legend:</h3>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <div className="flex items-center space-x-1.5">
                  <span className="text-sm">🟢</span>
                  <span className="text-xs sm:text-sm text-gray-600">Available</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <span className="text-sm">🔵</span>
                  <span className="text-xs sm:text-sm text-gray-600">Occupied</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <span className="text-sm">🟠</span>
                  <span className="text-xs sm:text-sm text-gray-600">Billing</span>
                </div>
              </div>
            </div>

            {/* Tables Grid */}
            {tables.length === 0 ? (
              <div className="card text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No tables added yet</p>
                <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              Add Your First Table
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {tables.map(table => {
              const status = getTableStatus(table.number);
              const tableOrders = getTableOrders(table.number);
              const totalItems = tableOrders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
              
              return (
                <div
                  key={table.id}
                  className={`border-2 rounded-xl p-4 ${getStatusColor(status)} hover:shadow-lg transition-all cursor-pointer`}
                >
                  {/* Table Number */}
                  <div className="text-center mb-3">
                    <div className="text-3xl mb-1">{getStatusIcon(status)}</div>
                    <h3 className="text-2xl font-bold">T{table.number}</h3>
                    <p className="text-sm opacity-75">
                      <Users className="w-4 h-4 inline mr-1" />
                      {table.capacity} seats
                    </p>
                  </div>

                  {/* Order Info */}
                  {status !== 'available' && (
                    <div className="text-center text-sm mb-3 py-2 bg-white bg-opacity-50 rounded">
                      <p className="font-semibold">{totalItems} items</p>
                      <p className="text-xs opacity-75">
                        {tableOrders.length} order{tableOrders.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={() => {
                        setEditingTable(table);
                        setFormData({ number: table.number, capacity: table.capacity });
                      }}
                      className="flex-1 p-2 bg-white rounded-lg hover:bg-opacity-80"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4 mx-auto" />
                    </button>
                    <button
                      onClick={() => setShowQRCode(table)}
                      className="flex-1 p-2 bg-white rounded-lg hover:bg-opacity-80"
                      title="View QR"
                    >
                      <QrCode className="w-4 h-4 mx-auto" />
                    </button>
                    <button
                      onClick={() => handleDeleteTable(table.id)}
                      className="flex-1 p-2 bg-white text-red-600 rounded-lg hover:bg-opacity-80"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </>
        )}
      </div>

      {/* Add/Edit Table Modal */}
      {(showAddModal || editingTable) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingTable ? 'Edit Table' : 'Add New Table'}
            </h2>
            <form onSubmit={editingTable ? handleUpdateTable : handleAddTable}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Table Number
                  </label>
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    className="input"
                    placeholder="e.g., 1, 2, 3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Capacity (Seats)
                  </label>
                  <input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    className="input"
                    min="1"
                    max="20"
                    required
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingTable(null);
                    setFormData({ number: '', capacity: 4 });
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingTable ? 'Update' : 'Add'} Table
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRCode && (
        <QRCodeGenerator
          table={showQRCode}
          restaurantId={restaurantId}
          onClose={() => setShowQRCode(null)}
        />
      )}
    </div>
  );
}

export default TableManagement;
